import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  CardContent, CircularProgress,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EastIcon from '@mui/icons-material/East';
import {useNavigate} from 'react-router';
import {SliderValueLabelProps} from '@mui/material/Slider';
import {pageStyles, StyledCard, StyledChip} from './styles';
import {getAllSubscriptions, getShopifyBuyUrl, getStripePrices} from "../../../../store/actions";
import {useAppDispatch} from "../../../../store/hooks";
import {IError} from "../../../../models/inner-models";
import Swal from "sweetalert2";
import {errorBigIcon} from "../../../../assets/icons/icons";
import {decoratePopUpMessage} from "../../../../utils/popUpTextDecorator";
import {useTranslation} from "react-i18next";

const essentialFeatures = [
  {text: 'AES 256-bit Encryption'},
  {text: 'Two-Factor Authentication'},
  {text: '30 day data retention history'},
  {text: 'Up to 1000 items'},
  {text: 'Automated daily backups'},
  {text: 'Download backups locally'},
  {text: 'Unlimited individual item restores'},
  {text: 'Email notifications'},
  {text: '24 hour email & chat support'},
];

const proFeatures = [
  {text: '90 day data retention history'},
  {text: 'Automated daily backups'},
  {text: 'Backup on demand'},
  {text: 'External storage option'},
  {text: 'Activate/deactivate backups'},
  {text: 'Organisations'},
  {text: 'Telephone support'},
  {text: 'Multiple users & admin control'},
  {text: 'Audit trail'},
];

const plans = [
  {
    name: 'ESSENTIAL',
    chipLabel: 'For individuals',
    description: 'Just want to automatically backup your data every 24 hours.',
    features: essentialFeatures,
  },
  {
    name: 'PRO',
    chipLabel: 'For small to medium organizations',
    description: 'You have more control over your backups.\nIncreased data retention periods, more frequent backups and telephone support.',
    features: proFeatures,
    hasEssentialFeatures: true,
  },
];

interface FeatureProps {
  text: string;
}

interface PlanProps {
  to: number;
  cost: number;
  planId: string
}

const FeatureItem = ({text}: FeatureProps) => (
  <ListItem disableGutters sx={pageStyles.featureItem}>
    <ListItemIcon sx={pageStyles.listItemIcon}>
      <CheckIcon sx={pageStyles.checkIcon}/>
    </ListItemIcon>
    <ListItemText primary={text}/>
  </ListItem>
);

const ShopifyPricesPage = () => {
  const {t: translate} = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(10);
  const [curVal, setCurVal] = useState(0);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const maxQuantity = 2000;
  const [stripeSub, setStripeSub] = useState({});
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [waitUrl, setWaitUrl] = useState<boolean>(false);
  const customSteps = [10, 300, 600, 1000, 2000, 2100]; // Define custom step positions
  const labels = [10, 300, 600, 1000, 2000, '2000+'];
  const stepsCount = labels.length;

  const createPriceCalculator = (planData: PlanProps[], isBtn?: boolean) => {
    return (quantity: number): PlanProps => {
      for (const tier of planData) {
        if (tier.to === null) tier.to = 2100;
        if (quantity <= tier.to) return tier
      }
      return {to: 0, cost: 0, planId: ''};
    };
  };

  const calculateEssentialPrice = createPriceCalculator(stripeSub?.essential?.month?.planCost || []);
  const calculateProPrice = createPriceCalculator(stripeSub?.pro_plan?.month?.planCost || []);

  const marks = customSteps.map((label, index) => ({
    value: (index / (stepsCount - 1)) * 100, // Generate values evenly spaced in the range [0, 100]
    label: labels[index]
  }));

  const handleSliderChange = (event: Event, newValue: number) => {
    const stepIndex = Math.round(((newValue as number) / 100) * (stepsCount - 1));
    setCurVal(newValue as number)
    setQuantity(customSteps[stepIndex]);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        await dispatch(getStripePrices()).unwrap().then(res => {
          if (res.result?.shopify)
            setStripeSub({...res.result?.shopify})
        })
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const buySubscription = async (planId) => {
    try {
      setWaitUrl(true);
      const resp = await dispatch(getShopifyBuyUrl(planId)).unwrap()
      window.open(resp?.confirmationUrl, '_blank')
      const time = 30000
      const interval = setInterval(()=>dispatch(getAllSubscriptions()), time)
      setTimeout(() => {
        setWaitUrl(false)
        clearInterval(interval);
        }, time * 10
      );
    } catch (e) {
      const error = e as IError;
      setWaitUrl(false);
      await Swal.fire({
        imageUrl: errorBigIcon,
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  }

  const ValueLabelComponent = (props: SliderValueLabelProps) => {
    const {children, value} = props;
    const stepIndex = Math.round(((value as number) / 100) * (stepsCount - 1));
    return (
      <Tooltip
        open
        placement="top-start"
        arrow
        title={stepIndex === 5 ? '2000+ orders to backup' : `Up to ${labels[stepIndex]} orders to backup`}
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '14px',
              backgroundColor: '#0283FA',
              borderRadius: '8px',
              padding: '10px',
              color: 'white',
              right: '50px',
            },
          },
          arrow: {
            sx: {
              marginLeft: '50px',
              '&:before': {
                backgroundColor: '#0283FA',
              },
            },
          },
        }}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <Container maxWidth="lg" sx={pageStyles.container}>
      <Typography variant="h4" gutterBottom sx={pageStyles.title}>
        Shopify: Plans and Pricing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={pageStyles.subtitle}>
        Pay only for what you backup
        <br/>
        <Box component="span" fontWeight="bold">
          Unlimited data recovery
        </Box>
      </Typography>

      <Box sx={pageStyles.sliderBox}>
        <Typography variant="body2" mb={13.5} ml={7.5} mt={2} sx={pageStyles.sliderLabel}>
          Choose how many orders to backup:
        </Typography>
        <Stack spacing={6} direction="row" alignItems="center" sx={pageStyles.sliderControl}>
          <Typography variant="body2" sx={{mr: 3, fontSize: '16px', whiteSpace: 'nowrap'}}>
            10
          </Typography>
          <Slider
            aria-label="Always visible"
            value={curVal}
            onChange={handleSliderChange}
            valueLabelDisplay="on"
            step={null}
            min={0}
            max={100}
            slots={{valueLabel: ValueLabelComponent}}
            marks={marks}
          />
          <Typography variant="body2" sx={{ml: 3, fontSize: '16px', whiteSpace: 'nowrap'}}>
            {maxQuantity}+
          </Typography>
        </Stack>
      </Box>

      {loadingData ?
        <div className="spinner-wrapper">
          <CircularProgress/>
        </div> :
        <Box sx={pageStyles.cardContainer}>
          {plans.map((plan, index) => {
            const price = plan.name === 'ESSENTIAL'
              ? calculateEssentialPrice(quantity).cost
              : calculateProPrice(quantity).cost
            const planId = plan.name === 'ESSENTIAL'
              ? calculateEssentialPrice(quantity).planId
              : calculateProPrice(quantity).planId
            return (
              <StyledCard key={index}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={7.5}>
                    <Typography variant="h5" fontWeight="bold" sx={pageStyles.titleText}>
                      {plan.name}
                    </Typography>
                    <StyledChip label={plan.chipLabel} size="small"/>
                  </Box>
                  <Box variant="h3" mb={4}>
                    <Box component="span" sx={pageStyles.priceText}>
                      {`$${price.toFixed(2)}`}
                    </Box>
                    <Box component="span" sx={pageStyles.monthText}>
                      /month
                    </Box>
                  </Box>
                  <Button variant="contained" fullWidth
                          disabled={waitUrl}
                          sx={{mb: 5, fontFamily: 'Readex Pro, sans-serif', textTransform: 'initial'}}
                          onClick={() => {buySubscription(planId)}}
                          endIcon={<EastIcon/>}
                  >
                    Buy Subscription
                  </Button>
                  <Typography variant="body2"
                              sx={{mb: 5, fontWeight: 400, fontSize: '14px', lineHeight: '18px', letterSpacing: '0%'}}
                              align="center">
                    14 days free access. No credit card required
                  </Typography>
                  <Box sx={pageStyles.descriptionBox}>
                    <Typography variant="body2" sx={{mb: 1, maxWidth: 300, ...pageStyles.descriptionText}}>
                      {plan.description}
                    </Typography>
                  </Box>
                  <List>
                    {plan.hasEssentialFeatures && showAllFeatures && (
                      <Typography variant="body2" sx={pageStyles.featuresTitle}>
                        Everything included in ESSENTIAL plus:
                      </Typography>
                    )}
                    {showAllFeatures ? plan.features.map((feature, featureIndex) => (
                        <FeatureItem key={featureIndex} text={feature.text}/>
                      )) :
                      <Button variant="text" fullWidth sx={{mt: 5}} onClick={() => setShowAllFeatures(true)}>
                        Show more
                      </Button>}
                  </List>
                </CardContent>
              </StyledCard>
            )
          })

          }
        </Box>
      }
    </Container>
  );
};

export default ShopifyPricesPage;
